import {
  Button,
  CircularProgress,
  Container,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Autocomplete } from "@material-ui/lab";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import CivitatisExperienceRepository from "../../repositories/civitatisExperienceRepository";
import { getSessionStorage } from "../../utils/getSessionStorage";
import { apiErrorMessage, isBlockingApiError } from "../../utils/checkoutErrors";

const useStyles = makeStyles((theme) => ({
  root: { backgroundColor: "#f2f2f2", minHeight: "100vh", paddingBottom: 40 },
  header: {
    backgroundColor: "#fff",
    padding: "16px 0",
    borderBottom: "1px solid #e0e0e0",
    marginBottom: 24,
  },
  breadcrumb: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    cursor: "pointer",
    color: "#c0392b",
    fontSize: 14,
    marginBottom: 8,
  },
  pageTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: "#1a1a2e",
  },
  section: {
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: "20px 24px",
    marginBottom: 16,
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 16,
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: "#1a1a2e",
  },
  sectionIcon: { color: "#c0392b", fontSize: "20px !important" },
  textField: { "& .MuiOutlinedInput-root": { borderRadius: 8 } },
  submitBtn: {
    backgroundColor: "#c0392b",
    color: "#fff",
    borderRadius: 8,
    padding: "12px 32px",
    fontSize: 15,
    fontWeight: 600,
    "&:hover": { backgroundColor: "#a93226" },
    "&:disabled": { backgroundColor: "#e0e0e0" },
  },
  loadingBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "60px 0",
    gap: 16,
    color: "#666",
  },
  errorBox: {
    backgroundColor: "#fff3f3",
    border: "1px solid #ffcccc",
    borderRadius: 8,
    padding: 16,
    color: "#c0392b",
    marginBottom: 16,
  },
}));

const civitatisRepo = new CivitatisExperienceRepository();

const FIELD_LABEL_MAP = {
  // Passport expiry must be checked before "passaporte" to avoid false match
  "validade passaporte": "passportExpiry",
  "passport expiry": "passportExpiry",
  "passport validity": "passportExpiry",
  "expiry date": "passportExpiry",
  "expiration date": "passportExpiry",
  validade: "passportExpiry",
  sobrenome: "surname",
  apelido: "surname",
  "last name": "surname",
  lastname: "surname",
  nome: "name",
  "first name": "name",
  firstname: "name",
  documento: "document",
  passaporte: "passport",
  identidade: "document",
  passport: "passport",
  document: "document",
  nascimento: "birthDate",
  birthdate: "birthDate",
  "birth date": "birthDate",
  email: "email",
  telefone: "phone",
  celular: "phone",
  phone: "phone",
  idade: "age",
  age: "age",
  nacionalidade: "nationality",
  nationality: "nationality",
};

const resolveFieldKey = (label = "") => {
  const lower = label.toLowerCase();
  // Check most-specific first
  const keys = Object.keys(FIELD_LABEL_MAP);
  keys.sort((a, b) => b.length - a.length);
  for (const k of keys) {
    if (lower.includes(k)) return FIELD_LABEL_MAP[k];
  }
  return null;
};

const buildEmptyPassenger = () => ({
  name: "",
  surname: "",
  document: "",
  passport: "",
  passportExpiry: "",
  birthDate: "",
  email: "",
  phone: "",
  age: "",
  nationality: "",
  extras: {},
});

export default function EntertainmentQuestions() {
  const classes = useStyles();
  const history = useHistory();

  const availability = getSessionStorage("@EntertainmentAvailabilitySelected") || {};
  const { date, session, paxCounts, adults = 1, children: storedChildren = 0, activity, activityId, refToken, totalPoints } = availability;
  const childCount = storedChildren || 0;

  // Derive passenger count + required count (same logic as checkout)
  const isGroupActivity = !!activity?.isCategoryPaxGroup;
  const rateCategories = session?.rates?.[0]?.categories || [];
  let totalPax = adults + childCount;
  let requiredPax = totalPax;
  if (isGroupActivity && paxCounts) {
    let slots = 0, req = 0;
    for (const cat of rateCategories) {
      const qty = paxCounts?.[cat.id] ?? paxCounts?.[String(cat.id)] ?? 0;
      // Group activities: qty = people in one group (capped at group.maximum on the detail page)
      if (qty > 0) {
        slots += qty;
        req += Math.min(qty, cat.group?.minimum ?? 1);
      }
    }
    if (slots > 0) { totalPax = slots; requiredPax = req; }
  }

  const [loading, setLoading] = useState(true);
  const [schema, setSchema] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [blocked, setBlocked] = useState(false);

  // One passenger entry per slot
  const [passengers, setPassengers] = useState(() =>
    Array.from({ length: Math.max(1, totalPax) }, buildEmptyPassenger),
  );
  const [bookingAnswers, setBookingAnswers] = useState({});

  // Build the rate object to pass to checkout-questions
  const buildRate = () => {
    if (session?.rates?.length) {
      const r = session.rates[0];
      if (r.categories?.length) {
        const cats = r.categories
          .map((cat) => {
            const qty = paxCounts != null ? (paxCounts[cat.id] ?? paxCounts[String(cat.id)] ?? 0) : 0;
            // Group activities book one service per group, regardless of people count
            return { id: cat.id, quantity: isGroupActivity ? Math.min(qty, 1) : qty };
          })
          .filter((c) => c.quantity > 0);
        return { id: r.id, categories: cats.length > 0 ? cats : [{ id: 0, quantity: adults }] };
      }
      return { id: r.id, categories: [{ id: 0, quantity: adults + childCount }] };
    }
    return { id: session?.id ?? 0, categories: [{ id: 0, quantity: adults + childCount }] };
  };

  // Scroll para o topo ao carregar a página
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchSchema = async () => {
      if (!activityId || !date || !session) {
        setLoading(false);
        return;
      }
      try {
        const result = await civitatisRepo.getCheckoutQuestions({
          activityId,
          refToken,
          date,
          session,
          rate: buildRate(),
          activityTime: session?.time ?? null,
        });
        setSchema(result);
      } catch (e) {
        if (isBlockingApiError(e)) {
          // Ex.: horário sem vaga — seguir só adiaria o mesmo erro no checkout.
          setApiError(apiErrorMessage(e));
          setBlocked(true);
        } else {
          setApiError("Não foi possível carregar as perguntas adicionais. Você pode continuar mesmo assim.");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchSchema();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updatePassenger = (idx, field, value) => {
    setPassengers((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const updatePassengerExtra = (idx, questionId, value) => {
    setPassengers((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], extras: { ...(next[idx].extras || {}), [questionId]: value } };
      return next;
    });
  };

  const handleSubmit = () => {
    // Persist pre-answers alongside existing availability data
    const updated = {
      ...availability,
      cartId: schema?.cartId ?? null,
      preAnswers: {
        passengers: passengers.map((p) => ({
          name: p.name.trim(),
          surname: p.surname.trim(),
          document: p.document.trim(),
          passport: p.passport || "",
          passportExpiry: p.passportExpiry || "",
          birthDate: p.birthDate,
          email: p.email.trim(),
          phone: p.phone.trim(),
          age: p.age,
          nationality: p.nationality || "",
          extras: p.extras || {},
        })),
        bookingAnswers: Object.entries(bookingAnswers).map(([id, value]) => ({ id, value })),
      },
    };
    sessionStorage.setItem("@EntertainmentAvailabilitySelected", JSON.stringify(updated));
    history.push("/busca/entretenimento/checkout");
  };

  const isValid = passengers.slice(0, requiredPax).every((p) => p.name.trim() && p.surname.trim());

  // Determine which passenger sub-fields to render beyond name/surname
  const extraPassengerFields = (schema?.passengerFields || []).filter((f) => {
    const key = resolveFieldKey(f.label);
    return key !== "name" && key !== "surname";
  });

  const renderPassengerField = (pax, idx, fieldKey, label, type, required, options) => {
    const value = fieldKey === "extras" ? "" : (pax[fieldKey] ?? "");

    if (type === "select" && Array.isArray(options) && options.length > 0) {
      const selectedOption = options.find((o) => String(o.id) === value) ?? null;
      return (
        <Grid item xs={12} sm={6} key={fieldKey}>
          <Autocomplete
            options={options}
            getOptionLabel={(o) => o.label || String(o.id)}
            value={selectedOption}
            onChange={(_, newVal) => updatePassenger(idx, fieldKey, newVal ? String(newVal.id) : "")}
            renderInput={(params) => (
              <TextField
                {...params}
                label={`${label}${required ? " *" : ""}`}
                variant="outlined"
                className={classes.textField}
              />
            )}
          />
        </Grid>
      );
    }

    return (
      <Grid item xs={12} sm={6} key={fieldKey}>
        <TextField
          label={`${label}${required ? " *" : ""}`}
          variant="outlined"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={value}
          onChange={(e) => updatePassenger(idx, fieldKey, e.target.value)}
          className={classes.textField}
          type={type === "number" || fieldKey === "age" ? "number" : (fieldKey === "birthDate" || fieldKey === "passportExpiry") ? "date" : "text"}
        />
      </Grid>
    );
  };

  const renderBookingQuestion = (q) => {
    const value = bookingAnswers[q.id] ?? "";
    if (q.type === "select" && q.options?.length) {
      // Use Autocomplete for large option lists (e.g. country/nationality pickers)
      if (q.options.length > 10) {
        const selectedOption = q.options.find((o) => String(o.id) === value) ?? null;
        return (
          <Grid item xs={12} sm={6} key={q.id}>
            <Autocomplete
              options={q.options}
              getOptionLabel={(o) => o.label || String(o.id)}
              value={selectedOption}
              onChange={(_, newVal) =>
                setBookingAnswers((prev) => ({ ...prev, [q.id]: newVal ? String(newVal.id) : "" }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`${q.label}${q.required ? " *" : ""}`}
                  variant="outlined"
                  className={classes.textField}
                />
              )}
            />
          </Grid>
        );
      }
      return (
        <Grid item xs={12} sm={6} key={q.id}>
          <Typography variant="caption" style={{ marginBottom: 4, display: "block", color: "#555" }}>
            {q.label}{q.required ? " *" : ""}
          </Typography>
          <Select
            fullWidth
            variant="outlined"
            value={value}
            onChange={(e) => setBookingAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
          >
            {q.options.map((o) => (
              <MenuItem key={o.id} value={String(o.id)}>{o.label}</MenuItem>
            ))}
          </Select>
        </Grid>
      );
    }
    return (
      <Grid item xs={12} sm={6} key={q.id}>
        <TextField
          label={`${q.label}${q.required ? " *" : ""}`}
          variant="outlined"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={value}
          onChange={(e) => setBookingAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
          className={classes.textField}
        />
      </Grid>
    );
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Container maxWidth="lg">
          <span className={classes.breadcrumb} onClick={() => history.goBack()}>
            <ArrowBackIcon style={{ fontSize: 16 }} />
            Voltar
          </span>
          <Typography className={classes.pageTitle}>Dados dos Passageiros</Typography>
        </Container>
      </div>

      <Container maxWidth="lg">
        {loading ? (
          <div className={classes.loadingBox}>
            <CircularProgress style={{ color: "#c0392b" }} />
            <span>Carregando perguntas adicionais...</span>
          </div>
        ) : (
          <>
            {apiError && <div className={classes.errorBox}>{apiError}</div>}

            {/* Booking-level questions (if any) */}
            {schema?.bookingQuestions?.length > 0 && (
              <div className={classes.section}>
                <Typography className={classes.sectionTitle}>Informações Adicionais</Typography>
                <Grid container spacing={2}>
                  {schema.bookingQuestions.map(renderBookingQuestion)}
                </Grid>
              </div>
            )}

            {/* Per-passenger fields */}
            {passengers.map((pax, idx) => {
              const isRequired = idx < requiredPax;
              return (
              <div key={idx} className={classes.section}>
                <Typography className={classes.sectionTitle}>
                  <PersonIcon className={classes.sectionIcon} />
                  {`Passageiro ${idx + 1}${!isRequired ? " (opcional)" : ""}`}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={isRequired ? "Nome *" : "Nome"}
                      variant="outlined"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      placeholder="Ex. JOÃO"
                      value={pax.name}
                      onChange={(e) => updatePassenger(idx, "name", e.target.value)}
                      className={classes.textField}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={isRequired ? "Sobrenome *" : "Sobrenome"}
                      variant="outlined"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      placeholder="Ex. SILVA"
                      value={pax.surname}
                      onChange={(e) => updatePassenger(idx, "surname", e.target.value)}
                      className={classes.textField}
                    />
                  </Grid>

                  {/* Extra passenger fields from schema (document, birthDate, etc.) */}
                  {extraPassengerFields.map((f) => {
                    const key = resolveFieldKey(f.label);
                    if (!key) {
                      const extraVal = pax.extras?.[f.label] ?? "";
                      if (f.type === "select" && Array.isArray(f.options) && f.options.length > 0) {
                        const selectedOption = f.options.find((o) => String(o.id) === extraVal) ?? null;
                        return (
                          <Grid item xs={12} sm={6} key={f.label}>
                            <Autocomplete
                              options={f.options}
                              getOptionLabel={(o) => o.label || String(o.id)}
                              value={selectedOption}
                              onChange={(_, newVal) => updatePassengerExtra(idx, f.label, newVal ? String(newVal.id) : "")}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={`${f.label}${f.required ? " *" : ""}`}
                                  variant="outlined"
                                  className={classes.textField}
                                />
                              )}
                            />
                          </Grid>
                        );
                      }
                      return (
                        <Grid item xs={12} sm={6} key={f.label}>
                          <TextField
                            label={`${f.label}${f.required ? " *" : ""}`}
                            variant="outlined"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={extraVal}
                            onChange={(e) => updatePassengerExtra(idx, f.label, e.target.value)}
                            className={classes.textField}
                          />
                        </Grid>
                      );
                    }
                    return renderPassengerField(pax, idx, key, f.label, f.type, f.required, f.options);
                  })}
                </Grid>
              </div>
              );
            })}

            <div style={{ textAlign: "right", marginTop: 8 }}>
              <Button
                className={classes.submitBtn}
                variant="contained"
                onClick={handleSubmit}
                disabled={!isValid || blocked}
              >
                Continuar
              </Button>
            </div>
          </>
        )}
      </Container>
    </div>
  );
}
